import { Component } from '@theme/component';

/**
 * <sticky-bar> — reveals a bottom-fixed pill (see sticky-bar.css) by toggling
 * `.sticky-bar--active` once a trigger condition is met. Two modes, chosen via `data-trigger`:
 *
 *  - "scroll" (default): reveal after the page has scrolled past `data-threshold` pixels
 *    (falls back to one viewport height). Used by the generic/collection contact bar, which
 *    has no add-to-cart button to anchor to.
 *  - "observe": reveal once the element matched by `data-observe` scrolls up out of the
 *    viewport — the exact behaviour of the variant-e product pill. Provided so the product
 *    sticky can adopt this shared engine later; no markup wires it up yet.
 *
 * The CSS owns the slide/fade animation and reduced-motion handling; this element only
 * toggles the active class.
 *
 * @extends {Component}
 */
export class StickyBar extends Component {
  /** @type {number} */
  #rafId = 0;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.trigger === 'observe') {
      this.#initObserve();
    } else {
      this.#initScroll();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.#observer?.disconnect();
  }

  #initScroll() {
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#onScroll();
  }

  // rAF-throttled so scroll stays cheap; innerHeight is read per-frame so the fallback
  // threshold tracks orientation/resize changes automatically.
  #onScroll = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      const parsed = Number(this.dataset.threshold);
      const threshold = Number.isFinite(parsed) && this.dataset.threshold !== '' ? parsed : window.innerHeight;
      this.classList.toggle('sticky-bar--active', window.scrollY > threshold);
    });
  };

  #initObserve() {
    const selector = this.dataset.observe;
    const target = selector ? document.querySelector(selector) : null;
    if (!target) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const isAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('sticky-bar--active', isAbove);
        }
      },
      { threshold: 0 }
    );
    this.#observer.observe(target);
  }
}

if (!customElements.get('sticky-bar')) {
  customElements.define('sticky-bar', StickyBar);
}
